// ── shareCanvas ───────────────────────────────────────────────────────────────
// Shared canvas primitives for NextEp's branded share artifacts (set pages,
// collection cards). Factored out of shareSetPage.js so every shareable image
// speaks the same visual language — the cinematic gradient, gold leaf, Georgia
// serif plates, poster tiles with a bottom scrim (Jason loves this look, so it's
// the house style for anything a NextEp link drops into a thread).

// Routed through our own img-proxy function, NOT image.tmdb.org directly — TMDB's
// CDN sends no CORS headers at all, so loadImage's crossOrigin='anonymous' below
// silently fails to load, and every canvas that drew straight from TMDB (Wrapped,
// collection cards, set pages) has been falling back to its no-image placeholder
// this whole time (found 2026-08-03). Same-origin via the proxy sidesteps the
// problem outright — same-origin draws can never taint a canvas.
export const img780 = (path) => `/.netlify/functions/img-proxy?p=${encodeURIComponent(path)}&s=w780`

export function loadImage(src) {
  return new Promise((resolve) => {
    if (!src) return resolve(null)
    const im = new Image()
    im.crossOrigin = 'anonymous'
    im.onload = () => resolve(im)
    im.onerror = () => resolve(null)
    im.src = src
  })
}

export function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath()
  ctx.moveTo(x + r, y)
  ctx.arcTo(x + w, y, x + w, y + h, r)
  ctx.arcTo(x + w, y + h, x, y + h, r)
  ctx.arcTo(x, y + h, x, y, r)
  ctx.arcTo(x, y, x + w, y, r)
  ctx.closePath()
}

// Draw an image cropped to cover a target rect (object-fit: cover).
export function drawCover(ctx, img, x, y, w, h) {
  const ir = img.width / img.height, tr = w / h
  let dw, dh, dx = x, dy = y
  if (ir > tr) { dh = h; dw = h * ir; dx = x - (dw - w) / 2 } else { dw = w; dh = w / ir; dy = y - (dh - h) / 2 }
  ctx.drawImage(img, dx, dy, dw, dh)
}

export function wrapText(ctx, text, x, y, maxW, lh, maxLines, center = false) {
  const words = String(text || '').split(' ')
  const lines = []; let line = ''
  for (const w of words) {
    const test = line ? line + ' ' + w : w
    if (ctx.measureText(test).width > maxW && line) { lines.push(line); line = w } else line = test
  }
  if (line) lines.push(line)
  let shown = lines.slice(0, maxLines)
  // Mark clipped overflow, then hard-fit each line so a single over-long word (or a
  // long title) can't bleed past the tile — truncate with an ellipsis to fit maxW.
  if (lines.length > maxLines && shown.length) shown[shown.length - 1] += '…'
  shown = shown.map(l => fitLine(ctx, l, maxW))
  const startY = center ? y - ((shown.length - 1) * lh) / 2 : y
  shown.forEach((l, i) => ctx.fillText(l, x, startY + i * lh))
}

// Truncate a string with an ellipsis until it fits maxW (character-level), for
// tokens too long to wrap on spaces.
function fitLine(ctx, str, maxW) {
  if (ctx.measureText(str).width <= maxW) return str
  let s = str
  while (s.length > 1 && ctx.measureText(s + '…').width > maxW) s = s.slice(0, -1)
  return s + '…'
}

// Wax "complete" seal — a small blood-red stamp with a serif mark.
export function drawWaxSeal(ctx, cx, cy, r) {
  ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.fillStyle = '#a3123a'; ctx.fill()
  ctx.lineWidth = 2; ctx.strokeStyle = '#7a0d2b'; ctx.stroke()
  ctx.fillStyle = '#ffd9e4'; ctx.font = '600 15px Georgia, serif'; ctx.textAlign = 'center'
  ctx.fillText('SET', cx, cy - 2); ctx.fillText('✦', cx, cy + 16); ctx.textAlign = 'left'
}

// Paint the standard cinematic background gradient onto a full canvas.
export function paintBackground(ctx, W, H) {
  const bg = ctx.createLinearGradient(0, 0, 0, H)
  bg.addColorStop(0, '#0d0c15'); bg.addColorStop(1, '#07070d')
  ctx.fillStyle = bg; ctx.fillRect(0, 0, W, H)
}
